const PREPARATION_RETRY_COUNT = 8;
const PREPARATION_RETRY_DELAY_MS = 500;

const base64UrlToUint8Array = (value) => {
    const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    const binary = window.atob(padded);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
};

const waitForPreparation = (signal) => new Promise((resolve, reject) => {
    if (signal && signal.aborted) {
        reject(new DOMException("The viewer request was aborted.", "AbortError"));
        return;
    }

    const timer = window.setTimeout(() => {
        if (signal) {
            signal.removeEventListener("abort", onAbort);
        }
        resolve();
    }, PREPARATION_RETRY_DELAY_MS);

    const onAbort = () => {
        window.clearTimeout(timer);
        reject(new DOMException("The viewer request was aborted.", "AbortError"));
    };

    if (signal) {
        signal.addEventListener("abort", onAbort, {once: true});
    }
});

const fetchViewerMetadata = async (url, headers, signal) => {
    for (let attempt = 0; attempt < PREPARATION_RETRY_COUNT; attempt += 1) {
        const response = await fetch(url, {
            headers,
            signal,
            cache: "no-store",
        });

        if (response.status === 409 && attempt < PREPARATION_RETRY_COUNT - 1) {
            await waitForPreparation(signal);
            continue;
        }

        if (!response.ok) {
            throw new Error(`The encrypted viewer metadata request failed (${response.status}).`);
        }

        return response.json();
    }

    throw new Error("The image is still being prepared.");
};

export const decryptViewerMetadata = async (metadata, signal) => {
    if (!window.crypto || !window.crypto.subtle) {
        throw new Error("Web Crypto is not available.");
    }

    const ciphertextResponse = await fetch(metadata.ciphertext_url, {
        signal,
        mode: "cors",
    });

    if (!ciphertextResponse.ok) {
        throw new Error(`The encrypted image request failed (${ciphertextResponse.status}).`);
    }

    const contentType = (ciphertextResponse.headers.get("content-type") || "").toLowerCase();
    if (!contentType.startsWith("application/octet-stream")) {
        throw new Error("The encrypted image response has an invalid content type.");
    }

    const ciphertext = new Uint8Array(await ciphertextResponse.arrayBuffer());
    const tag = base64UrlToUint8Array(metadata.tag);
    const encryptedPayload = new Uint8Array(ciphertext.length + tag.length);
    encryptedPayload.set(ciphertext);
    encryptedPayload.set(tag, ciphertext.length);

    const key = await window.crypto.subtle.importKey(
        "raw",
        base64UrlToUint8Array(metadata.key),
        {name: "AES-GCM"},
        false,
        ["decrypt"],
    );
    const plaintext = await window.crypto.subtle.decrypt(
        {
            name: "AES-GCM",
            iv: base64UrlToUint8Array(metadata.iv),
        },
        key,
        encryptedPayload,
    );

    return new Blob([plaintext], {type: metadata.mime});
};

const loadViewerImage = async ({metadataUrl, headers, signal}) => {
    const metadata = await fetchViewerMetadata(metadataUrl, headers, signal);
    return decryptViewerMetadata(metadata, signal);
};

export const loadCustomerViewerImage = ({galleryLink, viewerTicket, signal}) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const metadataUrl = `${apiUrl}/usergalery/${encodeURIComponent(galleryLink)}/viewer-key?ticket=${encodeURIComponent(viewerTicket)}`;

    return loadViewerImage({metadataUrl, headers: {}, signal});
};

export const loadAdminViewerImage = ({galeryId, imageId, token, signal}) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const metadataUrl = `${apiUrl}/galery/${encodeURIComponent(galeryId)}/images/${encodeURIComponent(imageId)}/viewer-key`;

    return loadViewerImage({
        metadataUrl,
        headers: {
            Authentication: `Bearer ${token}`,
        },
        signal,
    });
};